<template>
  <div
    ref="main"
    class="notification"
    tabindex="-1"
    @focusout="handleFocusOut"
    :class="{
      'is-open': isOpen,
      hasNews: hasNews,
      animateFadeIn: isStartup,
    }"
  >
    <button :aria-label="$t('notificationBox.title.new')" :aria-expanded="isOpen" aria-controls="notification-content"
            class="notification-bubble" @click="toggleState" v-on:keyup.enter="toggleState" tabindex="0">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:aria-hidden="'true'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M20 8l-8 5-8-5V6l8 5 8-5m0-2H4c-1.11 0-2 .89-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2z"/></svg>
    </button>
    <div id="notification-content"
         ref="content"
         class="notification-content"
         @click="toggleState"
         :class="{ hide: hideContent }"
         :style="styleContent"
    >
      <div ref="text" class="notification-text">
        <div class="notification-headline">{{ title }}</div>
        <div
          ref="description"
          class="notification-description"
          :style="styleDescription"
          v-show="isOpen"
        >
          {{ description }}
        </div>
      </div>
      <div class="notification-control"></div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapState, mapMutations } from "vuex";

export default defineComponent({
  props: {
    customTitle: {
      type: String,
    },
    description: {
      type: String,
    },
  },
  data() {
    return {
      title: "",
      isOpen: false,
      hideContent: false,
      isOpening: false,
      contentHeight: 0,
      descriptionHeight: 0,
      lastDescriptionHeight: 0,
    };
  },
  created() {
    this.setTitle();
    if (!this.hasNews) {
      this.hideContent = true;
    }
  },
  mounted() {
    this.calculateHeight();

    if (this.isStartup)
      document.addEventListener("animationend", this.handleAnimationEnd);

    document.addEventListener("scroll", this.handleScroll);
    document.addEventListener("transitionend", this.handleTransitionEnd);
  },
  methods: {
    translateTitle(key: string) {
      return this.$t("notificationBox.title." + key) as string;
    },
    setTitle() {
      this.title = this.hasNews
        ? this.translateTitle("new")
        : this.customTitle || this.translateTitle("open");
    },
    calculateHeight() {
      this.lastDescriptionHeight = this.descriptionHeight;

      this.descriptionHeight = (this.$refs
        .description as HTMLElement).scrollHeight;
      this.contentHeight =
        (this.$refs.text as HTMLElement).offsetHeight + this.descriptionHeight;
    },
    toggleState() {
      // Make sure that the content is not hidden before changing the state
      if (this.hideContent) {
        this.hideContent = false;
      }

      // When closing, or when opening for the first time, we want to immediatly open/close the box
      if (this.isOpen || (this.hasNews && this.lastDescriptionHeight == 0)) {
        this.isOpen = !this.isOpen;
        this.$nextTick().then(() => this.calculateHeight());
      } else {
        // When opening, after the box has been previously hidden, we don't want to immediatly open the box
        this.isOpening = true;
      }

      // When there are news and the box is now open, we disable the news
      if (this.hasNews && this.isOpen) {
        this.DISABLE_NEWS;
        this.setTitle();
      }
    },
    handleFocusOut(e: FocusEvent) {
      if (!this.isOpen) return;

      const mainDiv = this.$refs.main as Node;
      const target = e.relatedTarget as Node;

      if (mainDiv.contains(target)) return;

      this.isOpen = false;

      this.$nextTick().then(() => this.calculateHeight());
    },
    isElementVisible(element: Element) {
      const rect = element.getBoundingClientRect();
      const elemTop = rect.top;
      const elemBottom = rect.bottom;

      return elemTop < window.innerHeight && elemBottom >= 0;
    },
    handleScroll() {
      if (!this.isOpen) return;

      const content = this.$refs.content as Element;

      if (!this.isElementVisible(content)) this.toggleState();
    },
    handleAnimationEnd(e: AnimationEvent) {
      if (
        e.target != this.$refs.content ||
        !e.animationName.startsWith("content-fadeIn")
      )
        return;

      this.END_STARTUP;
      document.removeEventListener("animationend", this.handleAnimationEnd);
    },
    handleTransitionEnd(e: TransitionEvent) {
      if (e.target != this.$refs.content) return;

      // After the box is closed, we want to hide it
      if (
        e.propertyName == "height" &&
        !this.isOpen &&
        this.lastDescriptionHeight > 0
      ) {
        this.hideContent = true;
      } else if (e.propertyName == "transform" && this.isOpening) {
        // After the box is shown, we want to open it
        this.isOpen = true;
        this.isOpening = false;

        this.$nextTick().then(() => this.calculateHeight());
      }
    },
  },
  computed: {
    ...mapState(["isStartup", "hasNews"]),
    ...mapMutations(["END_STARTUP", "DISABLE_NEWS"]),

    styleContent(): { height: string } {
      return {
        height: this.contentHeight + "px",
      };
    },
    styleDescription(): { height: string } {
      return {
        height: this.descriptionHeight + "px",
      };
    },
  },
});
</script>

<style lang="scss" scoped>
$bubble-fadeIn-duration: 0.8s;
$content-fadeIn-duration: 0.5s;

$pulse-duration: 1.5s;
$pulse-image-scale: 0.9;
$pulse-shadow-size: 15px;
$pulse-shadow-opacity: 0.5;

.notification {
  display: flex;
  max-width: 338px;
  height: 60px;
  min-height: 60px;
  width: 100%;
  /* For Chrome/Safari on Android */
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  &:focus {
    outline: none;
  }

  &-bubble {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    background-color: $color-primary-blue;
    border-radius: 20px;
    fill: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 0 rgba($color-primary-blue, $pulse-shadow-opacity);
    cursor: pointer;

    svg {
      outline: none;
    }

    &:not(:disabled):focus, &:not(:disabled):active {
      outline: 2px solid #9B89D0;
    }
  }

  &-content {
    margin-top: -10px;
    margin-left: 18px;
    color: #fff;
    background-color: $color-primary-blue;
    position: relative;
    transition: height 0.4s ease-in-out, transform $content-fadeIn-duration ease,
      opacity $content-fadeIn-duration ease;
    min-height: 60px;
    flex-grow: 1;
    transform-origin: left center;
    cursor: pointer;

    &.hide {
      opacity: 0;
      transform: scaleX(0);
      cursor: default;
    }

    &::before {
      content: "";
      border-style: solid;
      border-width: 10px 13px 10px 0;
      border-color: transparent $color-primary-blue;
      position: absolute;
      left: -13px;
      top: 20px;
    }
  }

  &-text {
    display: flex;
    justify-content: center;
    flex-direction: column;
    padding: 15px 50px 15px 15px;
    font-size: 15px;
  }

  &-headline {
    font-weight: bold;
  }

  &-description {
    margin-top: 10px;
    overflow: hidden;
    transition: height 0.4s ease-in-out;
  }

  &-control {
    width: 44px;
    height: 44px;
    min-width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 2em;
    position: absolute;
    right: 2px;
    top: 7px;

    &::before,
    &::after {
      content: "";
      width: 18px;
      height: 3px;
      background-color: #fff;
      position: absolute;
      transition: transform 0.4s ease;
    }

    &::before {
      transform: rotateZ(-180deg);
    }

    &::after {
      transform: rotateZ(-90deg);
    }
  }

  &.hasNews {
    .notification {
      &-bubble {
        animation: pulse $pulse-duration infinite;
      }

      &-content {
        transition: height 0.4s ease-in-out,
          transform $content-fadeIn-duration ease;
      }
    }
  }

  &.animateFadeIn {
    .notification {
      &-bubble {
        animation: bubble-fadeIn $bubble-fadeIn-duration;
      }

      &-content {
        /* Opacity has to be set to 0, because there is a delay defined and we don't want to see the content before the animation */
        opacity: 0;
        animation: content-fadeIn $content-fadeIn-duration
          $bubble-fadeIn-duration;
      }
    }
  }

  &.is-open {
    .notification {
      &-control {
        &::before {
          transform: none;
        }

        &::after {
          transform: rotateZ(0deg);
        }
      }
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale($pulse-image-scale);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 $pulse-shadow-size rgba($color-primary-blue, 0);
  }

  100% {
    transform: scale($pulse-image-scale);
    box-shadow: 0 0 0 0 rgba($color-primary-blue, 0);
  }
}

@keyframes bubble-fadeIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }

  100% {
    transform: none;
    opacity: 1;
  }
}

@keyframes content-fadeIn {
  0% {
    transform: scaleX(0);
    opacity: 0;
  }

  100% {
    transform: none;
    opacity: 1;
  }
}
</style>
